import { createFileRoute, Link } from "@tanstack/react-router";
import { Container, CtaBand, Eyebrow, Lead } from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { publishedArticles } from "@/content/articles/meta";

const title = "Article archive | Scryla";
const description =
  "Every article published on Scryla since 2021, listed by year. Older pieces are kept as written and reflect the market at the time.";

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export const Route = createFileRoute("/articles/archive")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}/articles/archive` },
      ...ogImageMeta("article", "Article archive, every piece published since 2021"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/articles/archive` }],
  }),
  component: ArchivePage,
});

function ArchivePage() {
  const years = Array.from(new Set(publishedArticles().map((a) => a.date.slice(0, 4))));

  return (
    <div>
      <section
        aria-labelledby="archive-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Archive</Eyebrow>
          <h1
            id="archive-hero-heading"
            className="mt-4 text-[34px] leading-[1.06] font-bold tracking-[-0.035em] md:text-5xl"
          >
            The full archive
          </h1>
          <Lead className="mt-6">
            All {publishedArticles().length} articles, most recent first. Older pieces are kept as
            written and reflect the market at the time.{" "}
            <Link
              to="/articles"
              className="font-medium text-foreground underline decoration-border-strong underline-offset-4 hover:text-primary hover:decoration-primary"
            >
              Back to the curated index
            </Link>
            .
          </Lead>
        </Container>
      </section>

      {years.map((year) => {
        const posts = publishedArticles().filter((a) => a.date.startsWith(year));
        return (
          <section key={year} className="border-b border-border" aria-labelledby={`year-${year}`}>
            <Container>
              <h2
                id={`year-${year}`}
                className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase"
              >
                {year}
              </h2>
              <ul className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {posts.map((article) => (
                  <li
                    key={article.slug}
                    className="rounded-lg border border-border bg-card p-6 transition-shadow hover:shadow-card-hover"
                  >
                    <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                      <time dateTime={article.date}>{formatDate(article.date)}</time>
                    </p>
                    <h3 className="mt-3 text-lg font-semibold">
                      <Link
                        to="/articles/$slug"
                        params={{ slug: article.slug }}
                        className="hover:text-primary"
                      >
                        {article.title}
                      </Link>
                    </h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-body">
                      {article.description}
                    </p>
                  </li>
                ))}
              </ul>
            </Container>
          </section>
        );
      })}

      <CtaBand
        heading="Got a question the articles do not answer?"
        body="Tell me what is happening and I will tell you what I would do. A first conversation is free."
        to="/contact"
        cta="Get in touch"
      />
    </div>
  );
}
